import { useTranslation } from "react-i18next"
import { Clock, Info } from "lucide-react"

import { cn } from "@/shared/lib/utils"
import { formatNumber } from "@/shared/lib/staffing-shared"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import { Progress } from "@/shared/components/ui/progress"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip"

import type { DailySummaryTableProps } from "../types/daily-summary"

const GRADIENT_IMAGE_URL =
  "https://cdn.prod.website-files.com/689af392aa8c5cd6612c9ef7/689af392aa8c5cd6612c9f3f_gradient-map-2.webp"

export function DailySummaryTableSkeleton() {
  return (
    <div className="overflow-visible rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] animate-pulse">
      <div className="bg-[var(--color-surface-muted)]/60 h-10" />
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <div className="w-20 h-4 bg-[var(--color-border)]/60 rounded" />
          <div className="w-12 h-4 bg-[var(--color-border)]/50 rounded" />
          <div className="w-16 h-4 bg-[var(--color-border)]/50 rounded" />
          <div className="flex-1 h-2 bg-[var(--color-border)]/50 rounded-full" />
          <div className="w-12 h-4 bg-[var(--color-border)]/50 rounded" />
          <div className="w-12 h-4 bg-[var(--color-border)]/50 rounded" />
          <div className="w-8 h-6 bg-[var(--color-border)]/50 rounded-full" />
        </div>
      ))}
    </div>
  )
}

export function DailySummaryTable({
  data,
  isLoading = false,
  reportingInterval = "30min",
}: DailySummaryTableProps) {
  const { t } = useTranslation()

  if (isLoading || !data) {
    return <DailySummaryTableSkeleton />
  }

  const intervalLabel = t(`form.reportingIntervalOptions.${reportingInterval}`)

  return (
    <div className="overflow-visible rounded-xl bg-[var(--color-surface-muted)]/30">
      <div className="overflow-visible">
        <Table>
          <TableHeader className="bg-[var(--color-surface-muted)]/60">
            <TableRow className="hover:bg-transparent">
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)]">
                {t("dailySummary.columns.dayDate")}
              </TableHead>
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] text-right">
                {t("dailySummary.columns.callVolume")}
              </TableHead>
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] text-right">
                {t("dailySummary.columns.calls")}
              </TableHead>
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] min-w-[140px]">
                <span className="inline-flex items-center gap-1.5">
                  {t("dailySummary.columns.maxAgents")}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
                        aria-label={t("dailySummary.tooltips.maxAgentsInterval")}
                      >
                        <Info className="size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={4}>
                      {t("dailySummary.tooltips.maxAgentsInterval", { interval: intervalLabel })}
                    </TooltipContent>
                  </Tooltip>
                </span>
              </TableHead>
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] text-right">
                {t("dailySummary.columns.fteNeeded")}
              </TableHead>
              <TableHead className="border-b border-r border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] text-right">
                {t("dailySummary.columns.peopleHours")}
              </TableHead>
              <TableHead className="border-b border-[var(--color-border)] px-4 py-3 body-base font-medium text-[var(--color-text-primary)] text-center">
                {t("dailySummary.columns.typicalFte")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row, index) => {
              const isLastRow = index === data.rows.length - 1
              const borderB = isLastRow ? "" : "border-b"

              return (
                <TableRow key={index} className="hover:bg-[var(--color-surface-muted)]/50">
                  <TableCell
                    className={cn("border-r border-[var(--color-border)] px-4 py-3", borderB)}
                  >
                    <div className="body-sm text-[var(--color-text-primary)]">{t(row.dayKey)}</div>
                    <div className="body-xs text-[var(--color-text-secondary)]">{row.date}</div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "border-r border-[var(--color-border)] px-4 py-3 body-sm text-[var(--color-text-primary)] text-right",
                      borderB,
                    )}
                  >
                    {row.callVolumePercent}%
                  </TableCell>
                  <TableCell
                    className={cn(
                      "border-r border-[var(--color-border)] px-4 py-3 body-sm text-[var(--color-text-primary)] text-right",
                      borderB,
                    )}
                  >
                    {formatNumber(row.calls)}
                  </TableCell>
                  <TableCell
                    className={cn("border-r border-[var(--color-border)] px-4 py-3", borderB)}
                  >
                    <div className="flex items-center gap-2">
                      <Progress
                        value={(row.maxAgentsNeeded / data.maxAgentsOverall) * 100}
                        className="flex-1"
                        indicatorStyle={{
                          background: `linear-gradient(to right, rgba(255, 131, 8, 0.8), rgba(255, 80, 67, 0.8) 50%, rgba(57, 43, 213, 0.8)), url(${GRADIENT_IMAGE_URL})`,
                          backgroundSize: `100% ${data.rows.length * 100}%, 100% ${data.rows.length * 100}%`,
                          backgroundPosition: `0 ${data.rows.length > 1 ? (index / (data.rows.length - 1)) * 100 : 0}%, 0 ${data.rows.length > 1 ? (index / (data.rows.length - 1)) * 100 : 0}%`,
                          backgroundRepeat: "no-repeat",
                        }}
                      />
                      <span className="body-sm text-[var(--color-text-primary)] w-8 text-right">
                        {row.maxAgentsNeeded}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "border-r border-[var(--color-border)] px-4 py-3 body-sm text-[var(--color-text-primary)] text-right",
                      borderB,
                    )}
                  >
                    {formatNumber(row.fteNeeded, 1)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "border-r border-[var(--color-border)] px-4 py-3 body-sm text-[var(--color-text-primary)]",
                      borderB,
                    )}
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <Clock className="size-4 text-[var(--color-text-secondary)] opacity-50" />
                      {formatNumber(row.peopleHours, 1)}
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn("border-[var(--color-border)] px-4 py-3 text-center", borderB)}
                  >
                    <span
                      className={cn(
                        "inline-block min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full",
                        "bg-[var(--color-text-primary)] text-white",
                      )}
                    >
                      {row.typicalFte}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
